import { localEventDate } from '../../shared/eventTimezones';
import { RetrievedResultsPreview } from './RetrievedResultsPreview';
import { useRetrievedResultsFixture } from '../hooks/useRetrievedResultsFixture';
import type { SavedMatchComparison } from '../utils/retrievedResultsPreview';
import MatchResultsAutomationStatus from './MatchResultsAutomationStatus';
import MatchVerification from './MatchVerification';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { supabase } from '../config/supabase';
import { useAuth, useAuditUserId } from '../auth/AuthContext';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import type { MatchEventRecord } from '../types';
import { syncPlayedParticipationForCompletedMatch } from '../utils/matchResults';
import {
  buildScoreConfirmation,
  buildTeamResults,
  calculateTeamScore,
  compactSetScores,
  hasMatchLineScoreData,
  isMatchLineFinal,
  legacyLineResult,
  legacyResultValue,
  validateMatchLineScore,
  type MatchLineScoreDraft,
  type ScoreCompletionStatus,
  type ScoreLineStatus,
  type ScoreWinner,
} from '../utils/matchScoreEntry';
import { applyCoinTossResult, calculateTeamMatchResult, courtCaptainRulesetForPositions } from '../utils/teamMatchResultEngine';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import { scorecardPlayers, scorecardWinner } from '../utils/matchScorecard';

type MatchResultsProps = {
  match: MatchEventRecord;
  onSaved?: () => Promise<void>;
  onProviderResultChanged?: () => void;
  readOnly?: boolean;
  editorOpen?: boolean;
  onEditorOpen?: () => void;
  onEditorClose?: () => void;
  hideInlineSummary?: boolean;
  unifiedScorecard?: boolean;
};

type PositionRow = {
  id: string;
  position_code: string;
  display_name: string;
  display_order: number;
  line_type: string;
};

type MatchResultRow = {
  id: string;
  result_status: string;
  result_notes: string | null;
  coin_toss_winner_side: ScoreWinner | null;
  coin_toss_recorded_at: string | null;
};

type TeamOrganizationRow = {
  team_name?: string | null;
  league_name?: string | null;
  organizations?: { name?: string | null } | Array<{ name?: string | null }> | null;
};

type LineResultRow = {
  id: string;
  league_configuration_position_id: string;
  winner: ScoreWinner;
  completion_status: ScoreCompletionStatus;
  set1_us_games: number | null;
  set1_opponent_games: number | null;
  set2_us_games: number | null;
  set2_opponent_games: number | null;
  set3_us_games: number | null;
  set3_opponent_games: number | null;
  score_completeness: string | null;
  set3_is_match_tiebreak: boolean | null;
  match_tiebreak_exact_score_known: boolean | null;
  match_tiebreak_us_points: number | null;
  match_tiebreak_opponent_points: number | null;
  match_tiebreak_winner: ScoreWinner | null;
  retired: boolean;
  defaulted: boolean;
  walkover: boolean;
  notes: string | null;
  result_value: string;
};

type LineStatusRow = {
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
};

type LineupEntryRow = {
  league_configuration_position_id: string;
  slot_number: number | null;
  player_id: string;
  players?: { first_name?: string | null; last_name?: string | null } | Array<{ first_name?: string | null; last_name?: string | null }> | null;
};

type OfficialScorecard = {
  ourTeamName: string | null;
  opposingTeamName: string | null;
  lines: Array<{positionId: string; ourPlayers: Array<{slot: number;playerId: string | null;name: string;unresolved: boolean}>; opposingPlayers: string[]}>;
};

type LineDraft = MatchLineScoreDraft & { id?: string; ourPlayerNames?: string[] };
type ScoreEntryDraft = {
  lineDrafts: Record<string, LineDraft>;
  resultStatus: string;
  resultNotes: string;
};

const lineStatusOptions: ScoreLineStatus[] = ['Completed', 'Retired', 'Default', 'Double Default'];

function emptyLine(position: PositionRow): LineDraft {
  return {
    positionId: position.id,
    positionCode: position.position_code,
    displayName: position.display_name,
    displayOrder: position.display_order,
    lineType: position.line_type,
    winner: 'Unknown',
    status: 'Completed',
    completionStatus: 'Not Started',
    sets: [{ us: null, opponent: null }, { us: null, opponent: null }, { us: null, opponent: null }],
    retired: false,
    defaulted: false,
    walkover: false,
    set3IsMatchTiebreak: false,
    matchTiebreakExactScoreKnown: false,
    matchTiebreakUsPoints: null,
    matchTiebreakOpponentPoints: null,
    matchTiebreakWinner: 'Unknown',
    notes: '',
  };
}

function legacyWinner(resultValue: string): ScoreWinner {
  if (['Win', 'Retired Win', 'Default by Opponent'].includes(resultValue)) return 'Us';
  if (['Loss', 'Retired Loss', 'Default by Us'].includes(resultValue)) return 'Opponent';
  return 'Unknown';
}

function lineStatusFromResult(existing: LineResultRow): ScoreLineStatus {
  if (existing.result_value === 'Double Default') return 'Double Default';
  if (existing.retired || ['Retired Win', 'Retired Loss'].includes(existing.result_value)) return 'Retired';
  if (existing.defaulted || existing.walkover || ['Default by Opponent', 'Default by Us'].includes(existing.result_value)) return 'Default';
  return 'Completed';
}

function organizationNameForTeam(team: TeamOrganizationRow | null): string {
  const organization = Array.isArray(team?.organizations) ? team?.organizations[0] : team?.organizations;
  return String(organization?.name || team?.league_name || '').trim();
}

function nullableScore(value: string): number | null {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(99, Math.trunc(parsed))) : null;
}

function isSplitSetProxy(us: number | null | undefined, opponent: number | null | undefined): boolean {
  return (us === 1 && opponent === 0) || (us === 0 && opponent === 1);
}

function proxyWinner(us: number | null | undefined, opponent: number | null | undefined): ScoreWinner {
  if (us === 1 && opponent === 0) return 'Us';
  if (us === 0 && opponent === 1) return 'Opponent';
  return 'Unknown';
}

function enteredScore(us: number | null | undefined, opponent: number | null | undefined): boolean {
  return us != null && opponent != null;
}

function formatMatchDate(value: string | null | undefined): string {
  if (!value) return 'Date not set';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPlayerName(player: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  return [player?.first_name, player?.last_name].filter(Boolean).join(' ').trim();
}

function completedSetWinner(us: number | null | undefined, opponent: number | null | undefined): ScoreWinner {
  if (!enteredScore(us, opponent)) return 'Unknown';
  return Number(us) > Number(opponent) ? 'Us' : 'Opponent';
}

function scoreCompletenessFor(line: LineDraft): string {
  if (line.status !== 'Completed' || line.retired || line.defaulted || line.walkover) return 'Special Result';
  if (line.winner === 'Unknown') return 'Result Only';
  const [set1, set2, set3] = line.sets;
  const firstTwoComplete = set1?.us != null && set1.opponent != null && set2?.us != null && set2.opponent != null;
  if (!firstTwoComplete) return line.sets.some((set) => set.us != null || set.opponent != null) ? 'Partial' : 'Result Only';
  const split = (Number(set1.us) > Number(set1.opponent)) !== (Number(set2.us) > Number(set2.opponent));
  if (!split) return 'Complete';
  if (line.matchTiebreakExactScoreKnown && line.matchTiebreakUsPoints != null && line.matchTiebreakOpponentPoints != null) return 'Complete';
  if (isSplitSetProxy(set3?.us, set3?.opponent)) return 'Result Complete';
  return set3?.us != null && set3.opponent != null ? 'Complete' : 'Partial';
}

export function MatchResults({
  match,
  onSaved,
  onProviderResultChanged,
  readOnly = false,
  editorOpen,
  onEditorOpen,
  onEditorClose,
  hideInlineSummary = false,
  unifiedScorecard = false,
}: MatchResultsProps) {
  const auditUserId = useAuditUserId();
  const verificationAuth = useAuth();
  const canVerify = ['admin','owner'].includes(verificationAuth.appUser?.role || '') || verificationAuth.teamAssignments.some(a => a.team_id === match.team_id && a.is_active && !a.deleted_at && ['captain','co_captain'].includes(a.assignment_role) && a.matches_lineups_access !== 'none');
  const retrievedRecord = useRetrievedResultsFixture(match.id, canVerify);
  const [retrievedComparison, setRetrievedComparison] = useState<SavedMatchComparison>();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalOpen, setInternalOpen] = useState(false);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [lineDrafts, setLineDrafts] = useState<Record<string, LineDraft>>({});
  const [matchResult, setMatchResult] = useState<MatchResultRow | null>(null);
  const [teamName, setTeamName] = useState('Us');
  const [organizationName, setOrganizationName] = useState('');
  const [locationName, setLocationName] = useState('Location not set');
  const [resultStatus, setResultStatus] = useState('Not Started');
  const [resultNotes, setResultNotes] = useState('');
  const [savedDraftState, setSavedDraftState] = useState<ScoreEntryDraft>({ lineDrafts: {}, resultStatus: 'Not Started', resultNotes: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [officialScorecard, setOfficialScorecard] = useState<OfficialScorecard | null>(null);
  const [officialReadState, setOfficialReadState] = useState<'loading'|'applied'|'none'|'unavailable'>(unifiedScorecard && match.match_status === 'Completed' ? 'loading' : 'none');
  const [plannedPlayers, setPlannedPlayers] = useState<Record<string, Array<{slot:number;playerId:string;name:string}>>>({});

  useEffect(() => {
    if (!unifiedScorecard || match.match_status !== 'Completed') { setOfficialScorecard(null); setOfficialReadState('none'); return; }
    setOfficialReadState('loading');
    let active = true;
    void (async () => {
      try {
        const {data} = await supabase.auth.getSession();
        if (!data.session) { if (active) setOfficialReadState('unavailable'); return; }
        const response = await fetch(`/api/automation/match-results-status/${match.id}`, {headers:{Authorization:`Bearer ${data.session.access_token}`}});
        if (!response.ok) { if (active) setOfficialReadState('unavailable'); return; }
        const payload = await response.json() as {appliedAt?:string|null;officialScorecard?:OfficialScorecard};
        if (active) { setOfficialScorecard(payload.officialScorecard || null); setOfficialReadState(payload.appliedAt ? 'applied' : 'none'); }
      } catch { if (active) { setOfficialScorecard(null); setOfficialReadState('unavailable'); } }
    })();
    return () => { active = false; };
  }, [match.id, match.match_status, unifiedScorecard]);

  const open = editorOpen ?? internalOpen;
  const orderedLines = useMemo(
    () => positions.map((position) => lineDrafts[position.id] || emptyLine(position)),
    [lineDrafts, positions],
  );
  const summary = useMemo(() => calculateTeamScore(orderedLines), [orderedLines]);
  const isTcdMatch = /\bTCD\b/i.test(organizationName);
  const teamMatchRuleset = useMemo(() => courtCaptainRulesetForPositions(positions, isTcdMatch), [isTcdMatch, positions]);
  const baseTeamCalculation = useMemo(() => calculateTeamMatchResult(teamMatchRuleset, orderedLines), [orderedLines, teamMatchRuleset]);
  const recordedCoinTossWinner = matchResult?.coin_toss_winner_side === 'Us' || matchResult?.coin_toss_winner_side === 'Opponent'
    ? matchResult.coin_toss_winner_side
    : null;
  const teamCalculation = useMemo(
    () => applyCoinTossResult(baseTeamCalculation, recordedCoinTossWinner),
    [baseTeamCalculation, recordedCoinTossWinner],
  );
  const ourTeamLabel = teamName.trim() || 'Us';
  const opponentTeamLabel = match.opponent_name?.trim() || 'Opponent';
  const displayedOurScore = isTcdMatch ? teamCalculation.sets.Us.won : summary.ourWins;
  const displayedOpponentScore = isTcdMatch ? teamCalculation.sets.Opponent.won : summary.opponentWins;
  const winnerLabel = (winner: ScoreWinner) => winner === 'Us' ? ourTeamLabel : winner === 'Opponent' ? opponentTeamLabel : 'Unknown';
  const matchLevelResultLabel = match.match_status === 'Cancelled'
    ? 'Cancelled'
    : teamCalculation.status === 'winner_determined'
      ? `${teamCalculation.winner === 'Us' ? ourTeamLabel : opponentTeamLabel} — ${teamCalculation.explanation}`
      : teamCalculation.status === 'tie_determined'
        ? `Tie — ${teamCalculation.explanation}`
      : teamCalculation.status === 'coin_toss_required'
        ? 'Coin toss required'
        : teamCalculation.status === 'governing_body_decision_required'
          ? 'Governing-body decision required'
          : teamCalculation.status === 'manual_review'
            ? 'Manual review required'
    : match.match_status === 'Completed'
      ? summary.result
      : resultStatus === 'Not Started' && summary.result === 'Unknown'
        ? 'Not Played Yet'
        : resultStatus === 'Draft'
          ? 'In Progress'
          : summary.result === 'Unknown'
            ? 'Not Played Yet'
            : summary.result;
  const matchDetailItems = [
    { label: 'Team', value: ourTeamLabel },
    { label: 'Opponent', value: opponentTeamLabel },
    { label: 'Date', value: formatMatchDate(localEventDate(match)) },
    { label: 'Location', value: locationName },
  ];
  const currentDraftState = useMemo<ScoreEntryDraft>(
    () => ({ lineDrafts, resultStatus, resultNotes }),
    [lineDrafts, resultNotes, resultStatus],
  );
  const draftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: 'match-results',
    entityId: match.id,
    value: currentDraftState,
    initialValue: savedDraftState,
    enabled: open && positions.length > 0 && !readOnly,
  });

  const closeEditor = () => {
    if (editorOpen !== undefined) onEditorClose?.();
    else setInternalOpen(false);
  };

  const loadResults = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    const [positionsResult, matchResultResult, lineResultsResult, lineStatusesResult, teamResult, lineupEntriesResult, facilityResult] = await Promise.all([
      supabase
        .from('league_configuration_positions')
        .select('id, position_code, display_name, display_order, line_type')
        .eq('league_configuration_id', match.league_configuration_id)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('display_order'),
      supabase
        .from('match_results')
        .select('id, result_status, result_notes, coin_toss_winner_side, coin_toss_recorded_at')
        .eq('match_event_id', match.id)
        .is('deleted_at', null)
        .maybeSingle(),
      supabase
        .from('line_results')
        .select('id, league_configuration_position_id, winner, completion_status, set1_us_games, set1_opponent_games, set2_us_games, set2_opponent_games, set3_us_games, set3_opponent_games, score_completeness, set3_is_match_tiebreak, match_tiebreak_exact_score_known, match_tiebreak_us_points, match_tiebreak_opponent_points, match_tiebreak_winner, retired, defaulted, walkover, notes, result_value')
        .eq('match_event_id', match.id)
        .is('deleted_at', null),
      supabase
        .from('lineup_position_statuses')
        .select('league_configuration_position_id, line_status')
        .eq('match_event_id', match.id),
      supabase.from('teams').select('team_name, league_name, organizations(name)').eq('id', match.team_id).single(),
      supabase
        .from('lineup_entries')
        .select('league_configuration_position_id, slot_number, player_id, players(first_name, last_name)')
        .eq('match_event_id', match.id)
        .is('deleted_at', null)
        .order('slot_number'),
      match.facility_id
        ? supabase.from('facilities').select('facility_name').eq('id', match.facility_id).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ]);
    setLoading(false);

    const error = positionsResult.error || matchResultResult.error || lineResultsResult.error || lineStatusesResult.error || teamResult.error || lineupEntriesResult.error || facilityResult.error;
    if (error) {
      setMessage({ severity: 'error', text: error.message });
      return;
    }

    const nextPositions = (positionsResult.data || []) as PositionRow[];
    const existingByPosition = new Map(
      ((lineResultsResult.data || []) as unknown as LineResultRow[]).map((row) => [row.league_configuration_position_id, row]),
    );
    const statusByPosition = new Map(
      ((lineStatusesResult.data || []) as LineStatusRow[]).map((row) => [row.league_configuration_position_id, row.line_status]),
    );
    const playerNamesByPosition = ((lineupEntriesResult.data || []) as unknown as LineupEntryRow[]).reduce<Record<string, string[]>>((acc, row) => {
      const player = Array.isArray(row.players) ? row.players[0] : row.players;
      const name = formatPlayerName(player);
      if (!name) return acc;
      acc[row.league_configuration_position_id] ||= [];
      acc[row.league_configuration_position_id].push(name);
      return acc;
    }, {});
    setPlannedPlayers(((lineupEntriesResult.data || []) as unknown as LineupEntryRow[]).reduce<Record<string, Array<{slot:number;playerId:string;name:string}>>>((acc,row)=>{
      const player=Array.isArray(row.players)?row.players[0]:row.players;
      (acc[row.league_configuration_position_id] ||= []).push({slot:row.slot_number||0,playerId:row.player_id,name:formatPlayerName(player)});
      return acc;
    },{}));
    const drafts = nextPositions.reduce<Record<string, LineDraft>>((acc, position) => {
      const existing = existingByPosition.get(position.id);
      const blank = { ...emptyLine(position), ourPlayerNames: playerNamesByPosition[position.id] || [] };
      const lineStatus = statusByPosition.get(position.id);
      acc[position.id] = existing ? {
        ...blank,
        id: existing.id,
        winner: existing.winner || legacyWinner(existing.result_value),
        status: lineStatusFromResult(existing),
        completionStatus: existing.completion_status || (existing.result_value === 'Not Entered' ? 'Not Started' : 'Complete'),
        sets: [
          { us: existing.set1_us_games, opponent: existing.set1_opponent_games },
          { us: existing.set2_us_games, opponent: existing.set2_opponent_games },
          { us: existing.set3_us_games, opponent: existing.set3_opponent_games },
        ],
        retired: existing.retired,
        defaulted: existing.defaulted,
        walkover: existing.walkover,
        set3IsMatchTiebreak: existing.set3_is_match_tiebreak || isSplitSetProxy(existing.set3_us_games, existing.set3_opponent_games),
        matchTiebreakExactScoreKnown: existing.match_tiebreak_exact_score_known || false,
        matchTiebreakUsPoints: existing.match_tiebreak_us_points,
        matchTiebreakOpponentPoints: existing.match_tiebreak_opponent_points,
        matchTiebreakWinner: existing.match_tiebreak_winner || proxyWinner(existing.set3_us_games, existing.set3_opponent_games),
        notes: existing.notes || '',
      } : lineStatus === 'default_by_opponent' ? {
        ...blank,
        winner: 'Us',
        status: 'Default',
        completionStatus: 'Complete',
        defaulted: true,
      } : lineStatus === 'default_by_us' ? {
        ...blank,
        winner: 'Opponent',
        status: 'Default',
        completionStatus: 'Complete',
        defaulted: true,
      } : blank;
      return acc;
    }, {});
    const existingMatchResult = (matchResultResult.data || null) as MatchResultRow | null;

    setPositions(nextPositions);
    setLineDrafts(drafts);
    setMatchResult(existingMatchResult);
    const team = (teamResult.data as TeamOrganizationRow | null);
    setTeamName(team?.team_name || 'Us');
    setRetrievedComparison({
      matchId: match.id, date: match.match_date,
      side: match.match_site_type === 'Home' ? 'home' : match.match_site_type === 'Away' ? 'away' : null,
      teamName: team?.team_name || null, opponentName: match.opponent_name || null,
      lines: nextPositions.map(position => ({
        code: position.position_code, label: position.display_name || position.position_code, order: position.display_order, type: position.line_type,
        playerNames: playerNamesByPosition[position.id] || [], sets: drafts[position.id]?.sets || [],
      })),
    });
    setOrganizationName(organizationNameForTeam(team));
    setLocationName((facilityResult.data as { facility_name?: string } | null)?.facility_name || match.court_numbers || 'Location not set');
    setResultStatus(existingMatchResult?.result_status || 'Not Started');
    setResultNotes(existingMatchResult?.result_notes || '');
    setSavedDraftState({
      lineDrafts: drafts,
      resultStatus: existingMatchResult?.result_status || 'Not Started',
      resultNotes: existingMatchResult?.result_notes || '',
    });
  }, [match.court_numbers, match.facility_id, match.id, match.league_configuration_id, match.team_id]);

  useEffect(() => {
    void loadResults();
  }, [loadResults]);

  const updateLine = (positionId: string, patch: Partial<LineDraft>) => {
    setLineDrafts((current) => {
      const nextLine = { ...current[positionId], ...patch };
      if (patch.status) {
        nextLine.retired = patch.status === 'Retired';
        nextLine.defaulted = patch.status === 'Default' || patch.status === 'Double Default';
        nextLine.walkover = false;
        nextLine.completionStatus = 'Complete';
        if (patch.status === 'Double Default') nextLine.winner = 'Unknown';
      }
      if (patch.winner && patch.winner !== 'Unknown') nextLine.completionStatus = 'Complete';
      const next = { ...current, [positionId]: nextLine };
      if (resultStatus === 'Not Started') setResultStatus('Draft');
      return next;
    });
  };

  const updateSet = (positionId: string, setIndex: number, us: number | null, opponent: number | null) => {
    const line = lineDrafts[positionId];
    if (!line) return;
    const sets = line.sets.map((set, index) => index === setIndex ? { us, opponent } : set);
    const firstWinner = completedSetWinner(sets[0]?.us, sets[0]?.opponent);
    const secondWinner = completedSetWinner(sets[1]?.us, sets[1]?.opponent);
    const isStraightSetResult = firstWinner !== 'Unknown' && firstWinner === secondWinner;
    if (isStraightSetResult) sets[2] = { us: null, opponent: null };
    const tiebreakWinner = isStraightSetResult ? 'Unknown' : setIndex === 2 ? proxyWinner(us, opponent) : proxyWinner(sets[2]?.us, sets[2]?.opponent);
    updateLine(positionId, {
      sets,
      set3IsMatchTiebreak: isSplitSetProxy(sets[2]?.us, sets[2]?.opponent),
      matchTiebreakWinner: tiebreakWinner,
      matchTiebreakExactScoreKnown: isStraightSetResult ? false : line.matchTiebreakExactScoreKnown,
      matchTiebreakUsPoints: isStraightSetResult ? null : line.matchTiebreakUsPoints,
      matchTiebreakOpponentPoints: isStraightSetResult ? null : line.matchTiebreakOpponentPoints,
      completionStatus: line.completionStatus === 'Not Started' ? 'In Progress' : line.completionStatus,
    });
  };

  const saveResults = async () => {
    if (readOnly) return;
    if (resultStatus === 'Submitted') {
      setMessage({ severity: 'error', text: 'Submitted results are read-only. Ask an Owner or Admin for an audited correction.' });
      return;
    }
    const enteredLines = orderedLines.filter(hasMatchLineScoreData);
    if (enteredLines.length === 0) {
      setMessage({ severity: 'error', text: 'Enter at least one line result before saving.' });
      return;
    }
    const validationError = enteredLines.map(validateMatchLineScore).find(Boolean);
    if (validationError) {
      setMessage({ severity: 'error', text: validationError });
      return;
    }
    const allLinesComplete = orderedLines.length > 0 && orderedLines.every(isMatchLineFinal);
    setSaving(true);
    setMessage(null);
    const effectiveCoinTossWinner = baseTeamCalculation.status === 'coin_toss_required' ? recordedCoinTossWinner : null;
    const resultValue = teamCalculation.winner === 'Us' ? 'Win' : teamCalculation.winner === 'Opponent' ? 'Loss' : teamCalculation.status === 'tie_determined' ? 'Tie' : 'Unknown';
    const matchResultPayload = {
      match_event_id: match.id,
      result_status: 'Draft',
      our_team_score: allLinesComplete ? displayedOurScore : 0,
      opponent_team_score: allLinesComplete ? displayedOpponentScore : 0,
      team_result: allLinesComplete && resultValue === 'Win' ? 'win' : allLinesComplete && resultValue === 'Loss' ? 'loss' : 'tie',
      team_result_value: allLinesComplete ? resultValue : 'Unknown',
      calculation_status: allLinesComplete ? teamCalculation.status : 'pending',
      winner_determination_method: teamCalculation.determinationMethod,
      winning_team_id: teamCalculation.winner === 'Us' ? match.team_id : null,
      applied_ruleset_version: teamMatchRuleset.version,
      calculated_result_snapshot: teamCalculation,
      calculated_at: new Date().toISOString(),
      calculation_version: 1,
      coin_toss_winner_id: effectiveCoinTossWinner === 'Us' ? match.team_id : null,
      coin_toss_winner_side: effectiveCoinTossWinner,
      coin_toss_recorded_at: effectiveCoinTossWinner ? matchResult?.coin_toss_recorded_at || new Date().toISOString() : null,
      coin_toss_recorded_by: effectiveCoinTossWinner ? auditUserId : null,
      manual_score_override: false,
      result_notes: resultNotes.trim() || null,
      updated_by: auditUserId,
    };

    let matchResultId = matchResult?.id;
    if (matchResultId) {
      const { error } = await supabase.from('match_results').update(matchResultPayload).eq('id', matchResultId);
      if (error) {
        setSaving(false);
        setMessage({ severity: 'error', text: error.message });
        return;
      }
    } else {
      const { data, error } = await supabase.from('match_results')
        .insert({ ...matchResultPayload, created_by: auditUserId })
        .select('id')
        .single();
      if (error || !data) {
        setSaving(false);
        setMessage({ severity: 'error', text: error?.message || 'Unable to save match result.' });
        return;
      }
      matchResultId = String((data as { id: string }).id);
    }

    for (const line of enteredLines) {
      const payload = {
        match_result_id: matchResultId,
        match_event_id: match.id,
        league_configuration_position_id: line.positionId,
        position_code: line.positionCode,
        line_number: line.displayOrder,
        line_type: line.lineType,
        winner: line.winner,
        completion_status: 'Complete',
        set1_us_games: line.sets[0]?.us ?? null,
        set1_opponent_games: line.sets[0]?.opponent ?? null,
        set2_us_games: line.sets[1]?.us ?? null,
        set2_opponent_games: line.sets[1]?.opponent ?? null,
        set3_us_games: line.sets[2]?.us ?? null,
        set3_opponent_games: line.sets[2]?.opponent ?? null,
        score_completeness: scoreCompletenessFor(line),
        set3_is_match_tiebreak: Boolean(line.set3IsMatchTiebreak || isSplitSetProxy(line.sets[2]?.us, line.sets[2]?.opponent)),
        match_tiebreak_exact_score_known: Boolean(line.matchTiebreakExactScoreKnown),
        match_tiebreak_us_points: line.matchTiebreakExactScoreKnown ? line.matchTiebreakUsPoints ?? null : null,
        match_tiebreak_opponent_points: line.matchTiebreakExactScoreKnown ? line.matchTiebreakOpponentPoints ?? null : null,
        match_tiebreak_winner: line.matchTiebreakWinner && line.matchTiebreakWinner !== 'Unknown' ? line.matchTiebreakWinner : proxyWinner(line.sets[2]?.us, line.sets[2]?.opponent),
        set_scores: compactSetScores(line).join(' '),
        retired: line.status === 'Retired',
        defaulted: line.status === 'Default' || line.status === 'Double Default',
        walkover: false,
        line_result: legacyLineResult(line),
        result_value: legacyResultValue(line),
        notes: line.notes.trim() || null,
        updated_by: auditUserId,
      };
      const result = line.id
        ? await supabase.from('line_results').update(payload).eq('id', line.id)
        : await supabase.from('line_results').insert({ ...payload, created_by: auditUserId });
      if (result.error) {
        setSaving(false);
        setMessage({ severity: 'error', text: result.error.message });
        return;
      }
    }

    if (allLinesComplete) {
      const { error } = await supabase.from('match_results').update({
        result_status: 'Confirmed',
        updated_by: auditUserId,
      }).eq('id', matchResultId);
      if (error) {
        setSaving(false);
        setMessage({ severity: 'error', text: `Line scores saved, but the result status could not be updated: ${error.message}` });
        return;
      }
    }

    if (allLinesComplete) {
      try {
        await syncPlayedParticipationForCompletedMatch(match.id, auditUserId);
      } catch (error) {
        setMessage({ severity: 'error', text: `Scores saved, but participation sync failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setMessage({
      severity: 'success',
      text: allLinesComplete
        ? resultStatus === 'Submitted' ? 'Scores saved.' : 'Scores saved. Mark the scores Submitted to complete the match.'
        : 'Partial scores saved. Complete all lines before submitting the results.',
    });
    setSavedDraftState(currentDraftState);
    draftPersistence.clearDraft();
    await loadResults();
    await onSaved?.();
    closeEditor();
  };

  const markResultsSubmitted = async () => {
    if (readOnly || !matchResult || resultStatus !== 'Confirmed') return;
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from('match_results')
      .update({
        result_status: 'Submitted',
        updated_at: new Date().toISOString(),
        updated_by: auditUserId,
      })
      .eq('id', matchResult.id);
    setSaving(false);

    if (error) {
      setMessage({ severity: 'error', text: error.message });
      return;
    }

    setResultStatus('Submitted');
    setMessage({ severity: 'success', text: 'Results marked as submitted.' });
    await onSaved?.();
  };

  const recordCoinToss = async (winner: 'Us' | 'Opponent') => {
    if (readOnly || resultStatus === 'Submitted' || !matchResult || baseTeamCalculation.status !== 'coin_toss_required') return;
    const resolvedCalculation = applyCoinTossResult(baseTeamCalculation, winner);
    const now = new Date().toISOString();
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from('match_results')
      .update({
        result_status: 'Confirmed',
        our_team_score: displayedOurScore,
        opponent_team_score: displayedOpponentScore,
        team_result: winner === 'Us' ? 'win' : 'loss',
        team_result_value: winner === 'Us' ? 'Win' : 'Loss',
        calculation_status: resolvedCalculation.status,
        winner_determination_method: 'coin_toss',
        winning_team_id: winner === 'Us' ? match.team_id : null,
        applied_ruleset_version: teamMatchRuleset.version,
        calculated_result_snapshot: resolvedCalculation,
        calculated_at: now,
        coin_toss_winner_id: winner === 'Us' ? match.team_id : null,
        coin_toss_winner_side: winner,
        coin_toss_recorded_at: now,
        coin_toss_recorded_by: auditUserId,
        updated_at: now,
        updated_by: auditUserId,
      })
      .eq('id', matchResult.id);
    setSaving(false);
    if (error) {
      setMessage({ severity: 'error', text: error.message });
      return;
    }
    setMessage({ severity: 'success', text: `Coin toss recorded: ${winner === 'Us' ? ourTeamLabel : opponentTeamLabel} won the match.` });
    await loadResults();
    await onSaved?.();
  };

  const copyText = async (text: string, successText: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ severity: 'success', text: successText });
    } catch {
      setMessage({ severity: 'info', text });
    }
  };

  const renderSet = (line: LineDraft, setIndex: number) => {
    const set = line.sets[setIndex];
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Set {setIndex + 1}</Typography>
        <TextField
          aria-label={`${line.positionCode} Set ${setIndex + 1} ${ourTeamLabel}`}
          type="number"
          value={set.us ?? ''}
          disabled={readOnly}
          size="small"
          inputProps={{ min: 0, max: 99, inputMode: 'numeric', style: { fontSize: 20, textAlign: 'center' } }}
          onChange={(event) => updateSet(line.positionId, setIndex, nullableScore(event.target.value), set.opponent)}
        />
        <TextField
          aria-label={`${line.positionCode} Set ${setIndex + 1} ${opponentTeamLabel}`}
          type="number"
          value={set.opponent ?? ''}
          disabled={readOnly}
          size="small"
          inputProps={{ min: 0, max: 99, inputMode: 'numeric', style: { fontSize: 20, textAlign: 'center' } }}
          onChange={(event) => updateSet(line.positionId, setIndex, set.us, nullableScore(event.target.value))}
        />
      </Box>
    );
  };

  const retrievedPreview = canVerify ? <RetrievedResultsPreview record={retrievedRecord} snapshot={retrievedComparison} /> : null;
  if (positions.length === 0 && !loading) return retrievedPreview;

  return (
    <>
      {retrievedPreview}
      {canVerify && !unifiedScorecard && <><MatchResultsAutomationStatus matchId={match.id} /><MatchVerification matchId={match.id} readOnly={readOnly} onAcquisitionCompleted={onProviderResultChanged} /></>}
      <DraftRecoveryDialog
        open={Boolean(draftPersistence.recoverableDraft)}
        updatedAt={draftPersistence.recoverableDraft?.updatedAt}
        title="Unsaved Score Draft Found"
        onRestore={() => {
          const restored = draftPersistence.restoreDraft();
          if (!restored) return;
          setLineDrafts(restored.lineDrafts);
          setResultStatus(restored.resultStatus);
          setResultNotes(restored.resultNotes);
        }}
        onDiscard={draftPersistence.discardDraft}
      />
      {!hideInlineSummary && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{unifiedScorecard ? 'Match Scorecard' : 'Match Scores'}</Typography>
                {!unifiedScorecard && <>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{displayedOurScore}-{displayedOpponentScore}</Typography>
                  {isTcdMatch && <Typography variant="caption" color="text.secondary">Set points</Typography>}
                  <Typography variant="body2" color="text.secondary">{matchLevelResultLabel}</Typography>
                  <Typography variant="caption" color="text.secondary">{teamMatchRuleset.displayName} · v{teamMatchRuleset.version}</Typography>
                </>}
              </Box>
              <DraftSavedIndicator savedAt={draftPersistence.draftSavedAt} />
              {!readOnly && resultStatus !== 'Submitted' && (
                <Button
                  variant="contained"
                  startIcon={<SportsScoreIcon />}
                  onClick={() => editorOpen !== undefined ? onEditorOpen?.() : setInternalOpen(true)}
                  sx={{ minHeight: 48 }}
                >
                  Enter Scores
                </Button>
              )}
            </Box>
            {loading && <LinearProgress />}
            {message && <Alert severity={message.severity} sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Alert>}
            {baseTeamCalculation.status === 'coin_toss_required' && (
              <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Coin toss required</Typography>
                  <Typography variant="body2">
                    The match is tied after the configured line, set, and game tiebreakers. Record the coin-toss winner to finalize the team result.
                  </Typography>
                  {!readOnly && resultStatus !== 'Submitted' && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="contained" color="warning" disabled={saving || !matchResult} onClick={() => void recordCoinToss('Us')}>
                        {ourTeamLabel} won coin toss
                      </Button>
                      <Button variant="outlined" color="warning" disabled={saving || !matchResult} onClick={() => void recordCoinToss('Opponent')}>
                        {opponentTeamLabel} won coin toss
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Alert>
            )}
            <TableContainer sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
              <Table size="small" aria-label={unifiedScorecard ? 'Match scorecard' : 'Match line scores'} sx={{ minWidth: unifiedScorecard ? 920 : 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, ...(unifiedScorecard ? { position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' } : {}) }}>Line</TableCell>
                    {unifiedScorecard && <><TableCell sx={{ fontWeight: 700, minWidth: 165 }}>Our Players</TableCell><TableCell sx={{ fontWeight: 700, minWidth: 165 }}>Opposing Players</TableCell></>}
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Set 1</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Set 2</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Set 3</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Winner</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderedLines.map((line, index) => {
                    const officialLine = officialScorecard?.lines.find(item => item.positionId === line.positionId);
                    const {shown:ourNames} = scorecardPlayers(officialReadState === 'applied' ? [] : plannedPlayers[line.positionId] || [],officialLine?.ourPlayers,match.lineup_status === 'Published');
                    const publishedPlanned = [...(plannedPlayers[line.positionId] || [])].sort((a,b)=>a.slot-b.slot);
                    const lineupChanged = officialReadState === 'applied' && scorecardPlayers(publishedPlanned,officialLine?.ourPlayers,match.lineup_status === 'Published').changed;
                    const winnerPresentation = scorecardWinner(line.winner,officialScorecard?.ourTeamName,officialScorecard?.opposingTeamName,ourTeamLabel,opponentTeamLabel);
                    return <TableRow key={line.positionId} sx={{ bgcolor: index % 2 ? 'action.hover' : 'background.paper', '& td': { verticalAlign: 'top' } }}>
                      <TableCell sx={{ fontWeight: 700, position: unifiedScorecard ? 'sticky' : 'static', left: 0, zIndex: 1, bgcolor: index % 2 ? 'action.hover' : 'background.paper' }}>{line.positionCode}</TableCell>
                      {unifiedScorecard && <>
                        <TableCell>
                          <Stack spacing={0.25}>
                            {officialReadState === 'loading' ? <Typography variant="body2" color="text.secondary">Loading official lineup…</Typography> : officialReadState === 'unavailable' ? <Typography variant="body2" color="text.secondary">Not available</Typography> : ourNames.length ? ourNames.map((player, playerIndex) => <Typography key={`${player.slot}-${playerIndex}`} variant="body2" sx={{ overflowWrap: 'anywhere' }}>{player.name || 'Unresolved'}{player.unresolved && <Typography component="span" variant="caption" color="text.secondary"> · Unresolved</Typography>}</Typography>) : <Typography variant="body2" color="text.secondary">Not available</Typography>}
                            {lineupChanged && <Box component="details" sx={{ mt: 0.5 }}><Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary', fontSize: 12 }}>Lineup changed</Box><Typography variant="caption" color="text.secondary">Published: {publishedPlanned.map(player => player.name).join(' / ')}</Typography></Box>}
                          </Stack>
                        </TableCell>
                        <TableCell><Stack spacing={0.25}>{officialReadState === 'loading' ? <Typography variant="body2" color="text.secondary">Loading official lineup…</Typography> : officialLine?.opposingPlayers.length ? officialLine.opposingPlayers.map((name, playerIndex) => <Typography key={playerIndex} variant="body2" sx={{ overflowWrap: 'anywhere' }}>{name}</Typography>) : <Typography variant="body2" color="text.secondary">Not available</Typography>}</Stack></TableCell>
                      </>}
                      {line.sets.map((set, index) => (
                        <TableCell key={index} align="center">
                          {set.us !== null && set.opponent !== null ? `${set.us}-${set.opponent}` : '—'}
                          {unifiedScorecard && index === 2 && line.set3IsMatchTiebreak && set.us !== null && set.opponent !== null && <Typography variant="caption" component="div" color="text.secondary">Match TB</Typography>}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Chip
                          size="small"
                          label={unifiedScorecard ? winnerPresentation.label : winnerLabel(line.winner)}
                          color={unifiedScorecard ? winnerPresentation.color : line.winner === 'Us' ? 'success' : line.winner === 'Opponent' ? 'warning' : 'default'}
                          variant={line.winner === 'Unknown' ? 'outlined' : 'filled'}
                          sx={unifiedScorecard ? { height: 'auto', maxWidth: 210, '& .MuiChip-label': { display: 'block', whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 }, ...(line.winner !== 'Unknown' ? { color: 'common.white' } : {}) } : undefined}
                        />
                        {unifiedScorecard && line.status !== 'Completed' && <Typography variant="caption" component="div" color="text.secondary">{line.status}</Typography>}
                      </TableCell>
                    </TableRow>;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button startIcon={<ContentCopyIcon />} onClick={() => void copyText(buildScoreConfirmation(orderedLines), 'Score confirmation copied.')}>
                Copy Score Confirmation
              </Button>
              <Button startIcon={<ContentCopyIcon />} onClick={() => void copyText(buildTeamResults(orderedLines, teamName, match.opponent_name || 'Opponent'), 'Team results copied.')}>
                Copy Team Results
              </Button>
              {!readOnly && resultStatus === 'Confirmed' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  disabled={saving}
                  onClick={() => void markResultsSubmitted()}
                >
                  Mark as Submitted
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
      {canVerify && unifiedScorecard && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Official Result Review</Typography>
          <MatchVerification matchId={match.id} readOnly={readOnly} onAcquisitionCompleted={onProviderResultChanged} />
          <MatchResultsAutomationStatus matchId={match.id} hideActualLineup />
        </Box>
      )}

      <Dialog open={open} onClose={closeEditor} fullScreen={fullScreen} fullWidth maxWidth="md">
        <DialogTitle>Enter Scores</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                {matchDetailItems.map((item) => (
                  <Box key={item.label}>
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Team Score</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{summary.ourWins}-{summary.opponentWins}</Typography>
            </Paper>

            {orderedLines.map((line) => (
              <Paper key={line.positionId} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{line.positionCode}</Typography>
                    <Typography variant="body2" color="text.secondary">{line.displayName}</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr', gap: 1, alignItems: 'center' }}>
                    <Box />
                    <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{ourTeamLabel}</Typography>
                      {line.ourPlayerNames?.length ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {line.ourPlayerNames.join(' / ')}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center' }}>{opponentTeamLabel}</Typography>
                  </Box>
                  <RadioGroup value={line.winner} onChange={(event) => updateLine(line.positionId, { winner: event.target.value as ScoreWinner })}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Winner</Typography>
                      <FormControlLabel value="Us" disabled={readOnly || line.status === 'Double Default'} control={<Radio />} label="" sx={{ m: 0, justifyContent: 'center' }} />
                      <FormControlLabel value="Opponent" disabled={readOnly || line.status === 'Double Default'} control={<Radio />} label="" sx={{ m: 0, justifyContent: 'center' }} />
                    </Box>
                  </RadioGroup>
                  {line.sets.map((_, index) => <Box key={index}>{renderSet(line, index)}</Box>)}
                  {isSplitSetProxy(line.sets[2]?.us, line.sets[2]?.opponent) && (
                    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={Boolean(line.matchTiebreakExactScoreKnown)}
                              disabled={readOnly}
                              onChange={(event) => updateLine(line.positionId, {
                                matchTiebreakExactScoreKnown: event.target.checked,
                                matchTiebreakWinner: proxyWinner(line.sets[2]?.us, line.sets[2]?.opponent),
                              })}
                            />
                          )}
                          label="Exact match-tiebreak score is known"
                        />
                        {line.matchTiebreakExactScoreKnown && (
                          <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>MTB</Typography>
                            <TextField
                              aria-label={`${line.positionCode} Match Tiebreak ${ourTeamLabel} Points`}
                              type="number"
                              label={`${ourTeamLabel} points`}
                              value={line.matchTiebreakUsPoints ?? ''}
                              disabled={readOnly}
                              size="small"
                              inputProps={{ min: 0, max: 99, inputMode: 'numeric' }}
                              onChange={(event) => updateLine(line.positionId, {
                                matchTiebreakUsPoints: nullableScore(event.target.value),
                                matchTiebreakWinner: proxyWinner(line.sets[2]?.us, line.sets[2]?.opponent),
                              })}
                            />
                            <TextField
                              aria-label={`${line.positionCode} Match Tiebreak ${opponentTeamLabel} Points`}
                              type="number"
                              label={`${opponentTeamLabel} points`}
                              value={line.matchTiebreakOpponentPoints ?? ''}
                              disabled={readOnly}
                              size="small"
                              inputProps={{ min: 0, max: 99, inputMode: 'numeric' }}
                              onChange={(event) => updateLine(line.positionId, {
                                matchTiebreakOpponentPoints: nullableScore(event.target.value),
                                matchTiebreakWinner: proxyWinner(line.sets[2]?.us, line.sets[2]?.opponent),
                              })}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  )}
                  <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={line.status} disabled={readOnly} onChange={(event) => updateLine(line.positionId, { status: event.target.value as ScoreLineStatus })}>
                      {lineStatusOptions.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {line.status === 'Retired' && line.winner !== 'Unknown' && (
                    <Alert severity="info">
                      {line.winner === 'Us'
                        ? `${opponentTeamLabel} retired; ${ourTeamLabel} won.`
                        : `${ourTeamLabel} retired; ${opponentTeamLabel} won.`}
                    </Alert>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditor}>Cancel</Button>
          {!readOnly && resultStatus !== 'Submitted' && (
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving || loading} onClick={() => void saveResults()} sx={{ minHeight: 48 }}>
              Save Scores
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
